import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  FiDownload,
  FiEye,
  FiRepeat,
  FiLayers,
  FiPhone,
  FiUserCheck,
  FiUser,
  FiUsers,
  FiMousePointer,
} from "react-icons/fi";
import { TfiLocationPin } from "react-icons/tfi";
import api from "../../../../configs/api";
import { capitalize } from "../../../../utils/helpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const groupTrendData = (data, type) => {
  if (!data || data.length === 0) return [];

  const grouped = {};

  data.forEach((item) => {
    const date = new Date(item.date);

    let key = "";

    if (type === "Day") {
      // 18 Nov, 21 Nov ...
      key = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    if (type === "Week") {
      const start = new Date(date);
      const end = new Date(date);

      start.setDate(date.getDate() - date.getDay());
      end.setDate(start.getDate() + 6);

      key = `${start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })} - ${end.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })}`;
    }

    if (type === "Month") {
      // Nov 2025
      key = date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }

    if (type === "Year") {
      key = date.getFullYear().toString();
    }

    if (!grouped[key]) {
      grouped[key] = {
        dateKey: key,
        impressions: 0,
        views: 0,
        leadsGenerated: 0,
      };
    }

    grouped[key].impressions += item.impressions || 0;
    grouped[key].views += item.views || 0;
    grouped[key].leadsGenerated += item.leadsGenerated || 0;
  });

  return Object.values(grouped);
};

const MarketInsightsDetails = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("Day");

  const [propertyData, setPropertyData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPropertyInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/analytics/properties/performance/${id}`);
      console.log("Response Data==>", response);

      if (response.data.success) {
        const { property, summary, trend } = response.data;

        setPropertyData({
          propertyName: property.title || "N/A",
          category: property.type || "N/A",
          location: property.location || "N/A",
          thumbnail: property.thumbnail || null,
        });

        setAnalyticsData(
          summary || {
            views: 0,
            repeatViews: 0,
            impressions: 0,
            contactClicks: 0,
            phoneRevealClicks: 0,
            leadsGenerated: 0,
            loggedInViews: 0,
            guestViews: 0,
          }
        );

        setTrendData(trend || []);
      }
    } catch (err) {
      console.error("Failed to fetch property insights:", err);
      setError(err.response?.data?.message || "Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPropertyInsights();
    } else {
      setError("No property ID provided");
      setLoading(false);
    }
  }, [id, fetchPropertyInsights]);

  const filteredTrend = useMemo(() => {
    return groupTrendData(trendData, activeTab);
  }, [trendData, activeTab]);

  const getChartData = useMemo(() => {
    if (!filteredTrend || filteredTrend.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: "Impressions",
            data: [],
            borderColor: "#F59E0B", // Amber
            backgroundColor: "rgba(245, 158, 11, 0.15)",
            fill: true,
            tension: 0.4,
          },
          {
            label: "Views",
            data: [],
            borderColor: "#735DFF", // Purple
            backgroundColor: "rgba(115, 93, 255, 0.15)",
            fill: true,
            tension: 0.4,
          },
          {
            label: "Leads",
            data: [],
            borderColor: "#0CC763", // Green
            backgroundColor: "rgba(12, 199, 99, 0.15)",
            fill: true,
            tension: 0.4,
          },
        ],
      };
    }

    return {
      labels: filteredTrend.map((item) => item.dateKey),

      datasets: [
        {
          label: "Impressions",
          data: filteredTrend.map((item) => item.impressions),
          borderColor: "#F59E0B", // Amber
          backgroundColor: "rgba(245, 158, 11, 0.15)",
          fill: true,
          tension: 0.4,
        },
        {
          label: "Views",
          data: filteredTrend.map((item) => item.views),
          borderColor: "#735DFF", // Purple
          backgroundColor: "rgba(115, 93, 255, 0.15)",
          fill: true,
          tension: 0.4,
        },
        {
          label: "Leads",
          data: filteredTrend.map((item) => item.leadsGenerated),
          borderColor: "#0CC763", // Green
          backgroundColor: "rgba(12, 199, 99, 0.15)",
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [filteredTrend]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
      },
    },
  };

  const metricsData = useMemo(
    () => [
      {
        id: 1,
        label: "Impressions",
        value: analyticsData?.impressions || 0,
        icon: FiLayers,
        color: "#3B82F6", // Blue
      },
      {
        id: 2,
        label: "View Clicks",
        value: analyticsData?.viewClick || 0,
        icon: FiMousePointer,
        color: "#6366F1", // Indigo
      },
      {
        id: 3,
        label: "Views",
        value: analyticsData?.views || 0,
        icon: FiEye,
        color: "#8B5CF6", // Purple
      },
      {
        id: 4,
        label: "Contact Clicks",
        value: analyticsData?.contactClicks || 0,
        icon: FiPhone,
        color: "#F97316", // Orange
      },
      {
        id: 5,
        label: "Leads Generated",
        value: analyticsData?.leadsGenerated || 0,
        icon: FiUserCheck,
        color: "#22C55E", // Green
      },
      {
        id: 6,
        label: "Repeat Views",
        value: analyticsData?.repeatViews || 0,
        icon: FiRepeat,
        color: "#F59E0B", // Amber
      },
      {
        id: 7,
        label: "Logged In Views",
        value: analyticsData?.loggedInViews || 0,
        icon: FiUser,
        color: "#475569", // Slate
      },
      {
        id: 8,
        label: "Guest Views",
        value: analyticsData?.guestViews || 0,
        icon: FiUsers,
        color: "#14B8A6", // Teal
      },
    ],
    [analyticsData]
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-muted font-quicksand">Loading insights...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center font-quicksand">
          <div className="mb-4 text-red-500">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-red-500 mb-4 text-lg">{error}</p>
          <button
            onClick={fetchPropertyInsights}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!propertyData || !analyticsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center font-quicksand">
          <p className="text-text-muted text-lg">
            No data available for this property
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-quicksand bg-background py-3 sm:py-4 md:py-6 pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 md:pr-4">
      {/* Property Header */}
      <div className="mb-4 cursor-default font-poppins">
        <MarketInsightsHeader
          thumbnail={propertyData.thumbnail}
          title={propertyData.propertyName}
          category={propertyData.category}
          location={propertyData.location}
        />
      </div>

      <MetricsGrid metricsData={metricsData} />

      {/* Market Insights Chart */}
      <div className="bg-white/95 rounded-xl p-4 border border-gray-200 relative overflow-hidden transition-colors duration-300 ">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-5 py-3 border-b border-gray-200/70 gap-4">
          <h2 className="text-xl font-bold text-text-primary">
            Market Insights
          </h2>
          <div className="flex flex-wrap items-center rounded-lg">
            {["Day", "Week", "Month", "Year"].map((tab, index, arr) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium transition-colors 
                ${
                  activeTab === tab
                    ? "bg-primary text-white"
                    : "text-primary bg-primary/20"
                } 
                ${index !== arr.length - 1 ? "border-r border-primary/50" : ""} 
                ${index === 0 ? "rounded-l-md" : ""} 
                ${index === arr.length - 1 ? "rounded-r-md" : ""}`}
              >
                {tab}
              </button>
            ))}

            <button className="px-4 ml-2 py-2 rounded-md text-sm font-medium text-text-secondary bg-gray-100 hover:bg-gray-200 flex items-center gap-2 transition-colors duration-100">
              Export <FiDownload />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6 mt-6 mb-5 px-5 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
            <span className="text-sm text-text-secondary">Impressions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#735DFF]" />
            <span className="text-sm text-text-secondary">Views</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#0CC763]" />
            <span className="text-sm text-text-secondary">Leads</span>
          </div>
        </div>

        <div className="h-64 sm:h-80 px-4">
          {filteredTrend.length > 0 ? (
            <Line data={getChartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-text-muted">No trend data available yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MarketInsightsHeader = ({ thumbnail, title, category, location }) => {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 flex  gap-4 sm:gap-5 ">
      {/* Thumbnail */}
      <div className="flex-shrink-0 rounded-md overflow-hidden w-20 h-20 sm:w-24 sm:h-24 shadow-sm">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Text Section */}
      <div className="flex flex-1 grow flex-col  justify-between items-stretch">
        <div className="">
          <h1 className="text-lg sm:text-xl font-semibold text-text-primary  line-clamp-1">
            {title}
          </h1>
          <p className="text-text-muted mt-1 text-xs">
            Type: {capitalize(category)}
          </p>
        </div>

        {/* Location */}
        <div className="flex items-center  text-text-muted text-sm gap-1">
          <TfiLocationPin size={16} className="opacity-80" />
          <span className="line-clamp-1">{location}</span>
        </div>
      </div>
    </div>
  );
};

const MetricsGrid = ({ metricsData }) => {
  return (
    <div className="bg-white/95 rounded-xl p-4 border border-gray-200 relative overflow-hidden transition-colors duration-300 mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {metricsData.map((metric) => (
          <div
            key={metric.id}
            className="group bg-background-card rounded-xl p-6 shadow-lg border border-gray-200 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary"
          >
            {/* Hover Glow */}
            <div
              className="pointer-events-none absolute -right-7 -top-7 h-16 w-16 rounded-full opacity-0 group-hover:opacity-50 blur-2xl transition-opacity"
              style={{ backgroundColor: metric.color }}
            />

            {/* Icon + Value */}
            <div className="flex items-center justify-between mb-2">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{ backgroundColor: metric.color }}
              >
                <metric.icon className="text-white text-xl drop-shadow filter brightness-110" />
              </div>

              <h3 className="text-3xl font-extrabold text-text-primary leading-tight tracking-tight transition-colors group-hover:text-primary">
                {metric.value.toLocaleString()}
              </h3>
            </div>

            {/* Label */}
            <p className="text-text-muted text-[13px] mb-1 tracking-wide font-semibold uppercase">
              {metric.label}
            </p>

            {/* Bottom Gradient Line */}
            <div
              className="absolute left-5 right-5 bottom-0 h-0.5 rounded-full opacity-70"
              style={{
                background: `linear-gradient(to right, ${metric.color} 30%, transparent 70%)`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketInsightsDetails;
